import FormChoices from "../../../Components/FormChoices";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Lock, LockOpen } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Form } from "../../../system/useForm";
import { generateAssessmentCompanyChoices } from "../../../system/useCompanies";
import { Company, Compound, CompoundReference } from "../../../system/Domain";

interface Props {
	form: Form
	companies: Company[]
	compounds: Compound[]
	name: string
	deliveryPrefix?: string
	collectionPrefix?: string
	assessmentLocation: string
	assessmentType: string
}

type Rule = number | [number, number];

const rules: Rule[] = [
	// 1,
	// 12,
	// [20, 31],
	// 81,
	// [90, 99]
];

const matchesRules = (prefix: number, rules: Rule[]): boolean => rules.some(rule => {
	if (typeof rule === "number") return prefix === rule;
	const [min, max] = rule;
	return prefix >= min && prefix <= max;
});

const checkString = (str: string): boolean => {
	if (!str) return false;

	const prefixStr = str.slice(0, 2);
	if (!/^\d{2}$/.test(prefixStr)) return false;

	const prefix = parseInt(prefixStr, 10);
	return matchesRules(prefix, rules);
};

const AssessorCompanyChoice = (props: Props) => {
	const [locked, setLocked] = useState(true);

	const deliveryPrefix = props.deliveryPrefix || "";
	const collectionPrefix = props.collectionPrefix || "";

	const dekra = props.companies?.filter(f => f.id === 20)[0];
	const macadam = props.companies?.filter(f => f.id === 15)[0];

	const isRestwertGutachten = props.form.getValue(props.assessmentType) === "Restwertgutachten";

	const chooseAssessor = (form: Form) => {
		let targetValue: string;

		// (1) only Dekra does Restwertgutachten
		if (isRestwertGutachten) {
			targetValue = dekra.id.toString();

			if (!locked) setLocked(true);
		} else {
			if (!locked) return;

			const target = form.getValue(props.assessmentLocation);

			const targetCompound: CompoundReference = target === "DeliveryAddress"
				? form.getValue(`${deliveryPrefix}compound`)
				: form.getValue(`${collectionPrefix}compound`);

			// (2) if target is a compound, so the default assessor of this compound has to be selected
			if (targetCompound) {
				const compound = targetCompound.id
					?
					props.compounds.filter(s => s.id === targetCompound.id)[0]
					:
					props.compounds.filter(s => s.name === targetCompound.name)[0];

				targetValue = compound.defaultAssessor.id.toString();
			} else {
				const targetZip = target === "DeliveryAddress"
					? form.getValue(`${deliveryPrefix}address.zipCode`)
					: form.getValue(`${collectionPrefix}address.zipCode`);

				if (checkString(targetZip)) {
					targetValue = macadam.id.toString();
				} else {
					targetValue = dekra.id.toString();
				}
			}
		}

		if (form.getValue(props.name) !== targetValue) {
			form.setValue(props.name, targetValue);
		}
	};

	useEffect(() => {
		chooseAssessor(props.form);
	}, [props.form, locked]);

	return (
		<Box mb={2} sx={{ display: "inline-flex" }}>
			<FormChoices
				name={props.name}
				label="Gutachterunternehmen"
				choices={generateAssessmentCompanyChoices(props.companies)}
				form={props.form}
				disabled={locked}
				options={{ required: true }}
				stringify
			/>
			<Box sx={{ display: "flex", alignItems: "end", justifyContent: "center", marginLeft: 2 }}>
				<Tooltip
					title={
						<Box>
							<Box>Restwertgutachten werden ausschließlich von der Dekra gemacht.</Box>
							{/*<Box mt={2}>Für <strong>externe</strong> Gutachten erhält in den folgenden Postleitzahlenbereichen (erste 2 Ziffern) <strong>Macadam</strong> den Gutachtenauftrag</Box>*/}
						</Box>
					}
				>
					<Box>
						<IconButton sx={{ marginRight: 4 }} onClick={() => setLocked(e => !e)} disabled={isRestwertGutachten}>
							{locked && (
								<Lock />
							)}
							{!locked && (
								<LockOpen />
							)}
						</IconButton>
					</Box>
				</Tooltip>
			</Box>
		</Box>
	);
};

export default AssessorCompanyChoice;
